// eTAMS — Create expense claim (PDF page 6). Also edits drafts / returned
// claims (?id=) and can start from an approved journey (?sanction=).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { expenseAPI, etamsAPI, uploadAttachment } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Check, Note, Loading, Spin, FileDrop, FileChip, Modal, Empty,
  useAsync, useProjects, useQueryParam, money, isoDate, fmtRange, fmtDate,
} from '../../components/ui';
import { notify } from '../../utils/notify';
import { EXPENSE_CATEGORIES, PAID_BY, MODES_OF_TRAVEL, DOC_TO_CATEGORY, attachmentsOf, idOf } from '../../utils/etams';

const line = (date = '') => ({ expenseType: 'Rail Fare', amount: '', gst: '', paidBy: 'Employee', billReference: '', date, description: '' });

export default function CreateExpenseClaim() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const id = useQueryParam('id');
  const sanctionParam = useQueryParam('sanction');
  const projects = useProjects();
  const src = useAsync(() => Promise.all([
    etamsAPI.listTravel({ scope: 'mine', status: 'APPROVED,COMPLETED' }).then((d) => d.requests || []).catch(() => []),
    expenseAPI.listAdvances({ limit: 200 }).then((d) => (d.requests || []).filter((a) => idOf(a.requestedBy) === String(user?.id))).catch(() => []),
    etamsAPI.listReceipts({ status: 'UNASSIGNED' }).then((d) => d.receipts || []).catch(() => []),
    id ? expenseAPI.getClaim(id).then((d) => d.claim) : Promise.resolve(null),
  ]), [id]);
  const [sanctions, advances, receipts, existing] = src.data || [[], [], [], null];

  const [f, setF] = useState({ reportTitle: '', projectCode: '', sanction: '', from: '', to: '', location: '', mode: MODES_OF_TRAVEL[0], tourReportRef: '', sharedExpenses: 'None', declaration: false });
  const [lines, setLines] = useState([line()]);
  const [newFiles, setNewFiles] = useState([]);
  const [pickedReceipts, setPickedReceipts] = useState([]);
  const [receiptPicker, setReceiptPicker] = useState(false);
  const [saving, setSaving] = useState(null);

  // Load existing claim / preselect sanction
  useEffect(() => {
    if (existing) {
      setF({
        reportTitle: existing.reportTitle || existing.purposeOfTravel || '', projectCode: existing.projectCode || '',
        sanction: existing.travelRequestId ? idOf(existing.travelRequestId) : '', from: isoDate(existing.travelStartDate), to: isoDate(existing.travelEndDate),
        location: existing.travelLocation || '', mode: existing.modeOfTravel || MODES_OF_TRAVEL[0], tourReportRef: existing.tourReportRef || '',
        sharedExpenses: existing.sharedExpenses || 'None', declaration: !!existing.declarationAccepted,
      });
      setLines((existing.expenses || []).length ? existing.expenses.map((e) => ({
        expenseType: e.expenseType || 'Others', amount: e.amount ?? '', gst: e.gst ?? '', paidBy: e.paidBy || 'Employee', billReference: e.billReference || '', date: isoDate(e.date), description: e.description || '',
      })) : [line(isoDate(existing.travelStartDate))]);
    } else if (sanctionParam && sanctions.length) {
      pickSanction(sanctionParam);
    }
  }, [existing, sanctions.length, sanctionParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  function pickSanction(sid) {
    const t = sanctions.find((x) => x._id === sid);
    setF((p) => ({
      ...p, sanction: sid,
      ...(t ? {
        reportTitle: p.reportTitle || t.purpose || `${t.destination} visit`, projectCode: p.projectCode || t.projectCode || '',
        from: isoDate(t.departureDate), to: isoDate(t.returnDate), location: t.destination, mode: t.modeOfTravel || p.mode,
      } : {}),
    }));
    setLines((ls) => ls.map((l) => ({ ...l, date: l.date || isoDate(t?.departureDate) })));
  }

  // Advance that will be adjusted
  const linkedAdvance = useMemo(() => {
    if (existing?.linkedAdvanceId) return { advanceReleasedAmount: existing.advanceReceived, requestId: existing.linkedAdvanceId?.requestId, _id: idOf(existing.linkedAdvanceId), preLinked: true };
    const unlinked = (advances || []).filter((a) => a.status === 'APPROVED' && !a.linkedClaimId);
    if (f.sanction) return unlinked.find((a) => idOf(a.travelRequestId) === f.sanction) || null;
    return unlinked.sort((a, b) => new Date(b.advanceReleasedAt || 0) - new Date(a.advanceReleasedAt || 0))[0] || null;
  }, [existing, advances, f.sanction]);
  const advance = Number(linkedAdvance?.advanceReleasedAmount) || 0;

  const totalOf = (l) => (Number(l.amount) || 0) + (Number(l.gst) || 0);
  const employee = lines.filter((l) => l.paidBy === 'Employee').reduce((s, l) => s + totalOf(l), 0);
  const company = lines.filter((l) => l.paidBy !== 'Employee').reduce((s, l) => s + totalOf(l), 0);
  const net = employee - advance;
  const receiptTotal = pickedReceipts.reduce((s, r) => s + (r.amount || 0), 0);

  const addFromReceipt = (r) => {
    setPickedReceipts((p) => (p.some((x) => x._id === r._id) ? p : [...p, r]));
    setLines((ls) => {
      const blankIdx = ls.findIndex((l) => !l.amount && !l.billReference);
      const nl = { expenseType: r.category || DOC_TO_CATEGORY[r.documentType] || 'Others', amount: Math.max(0, (r.amount || 0) - (r.gst || 0)) || '', gst: r.gst || '', paidBy: 'Employee', billReference: r.billNumber || r.file?.fileName || '', date: isoDate(r.expenseDate) || f.from, description: r.vendorName || r.documentType };
      if (blankIdx >= 0) return ls.map((l, j) => (j === blankIdx ? nl : l));
      return [...ls, nl];
    });
  };

  const save = async (submit) => {
    if (submit) {
      const miss = [];
      if (!f.reportTitle.trim()) miss.push('Report title');
      if (!f.projectCode) miss.push('Project');
      if (!f.from || !f.to) miss.push('Travel (from – to)');
      if (!f.location.trim()) miss.push('Location');
      if (miss.length) { notify.error(`Please fill: ${miss.join(', ')}`); return; }
      if (new Date(f.to) < new Date(f.from)) { notify.error('Travel end date is before the start date'); return; }
      const bad = lines.findIndex((l) => !(Number(l.amount) > 0) || !l.billReference.trim());
      if (!lines.length || bad >= 0) { notify.error(bad >= 0 ? `Line ${bad + 1}: enter base amount and bill reference` : 'Add at least one expense line'); return; }
      if (!f.declaration) { notify.error('Please confirm the expenses are official and not claimed twice'); return; }
    }
    setSaving(submit ? 'submit' : 'draft');
    try {
      let uploaded = [];
      if (newFiles.length) {
        try { uploaded = await Promise.all(newFiles.map((file) => uploadAttachment(file, 'expense'))); } catch { notify.warn('Some files could not be uploaded — continuing without them'); }
      }
      const payload = {
        reportTitle: f.reportTitle, purposeOfTravel: f.reportTitle, projectCode: f.projectCode,
        travelRequestId: f.sanction || '', travelRequestNo: sanctions.find((s) => s._id === f.sanction)?.requestNo || '',
        travelStartDate: f.from, travelEndDate: f.to, travelLocation: f.location, modeOfTravel: f.mode,
        tourReportRef: f.tourReportRef, sharedExpenses: f.sharedExpenses, declarationAccepted: f.declaration,
        expenses: lines.filter((l) => l.amount !== '' || l.billReference).map((l) => ({
          expenseType: l.expenseType, amount: Number(l.amount) || 0, gst: Number(l.gst) || 0, total: totalOf(l),
          paidBy: l.paidBy, billReference: l.billReference, description: l.description, date: l.date || f.from || new Date().toISOString(),
        })),
        ...(linkedAdvance && !linkedAdvance.preLinked ? { linkedAdvanceId: linkedAdvance._id } : {}),
      };
      const allAttachments = [...attachmentsOf(existing), ...uploaded];
      if (allAttachments.length) payload.attachments = allAttachments;
      let claim;
      if (id) {
        const r = await expenseAPI.updateClaim(id, payload);
        claim = r.claim;
        if (submit) await expenseAPI.submitClaim(id);
      } else {
        const r = await expenseAPI.createClaim({ ...payload, saveAsDraft: !submit });
        claim = r.claim;
      }
      for (const rc of pickedReceipts) {
        // eslint-disable-next-line no-await-in-loop
        await etamsAPI.assignReceipt(rc._id, claim._id).catch(() => {});
      }
      notify.success(submit ? `Claim ${claim.claimId} submitted for approval` : 'Draft saved');
      navigate('/expense/claim-submission');
    } catch (e) {
      notify.error(e.message || 'Could not save the claim');
    } finally {
      setSaving(null);
    }
  };

  if (src.loading) return <PageShell section="Travel & Expenses"><Loading /></PageShell>;
  const readOnly = existing && existing.status !== 'DRAFT';
  const returned = existing && (existing.approvalHistory || []).filter((h) => h.action === 'RETURNED').slice(-1)[0];

  return (
    <PageShell section="Travel & Expenses">
      <PageHead title={existing ? `Expense claim ${existing.claimId}` : 'Create expense claim'} subtitle="TSE-HR-TEC-001 · Proposed Rev. 00" />
      {readOnly && <Note tone="info" className="mb-18">This claim has been submitted and can no longer be edited here.</Note>}
      {existing?.status === 'DRAFT' && returned && <Note tone="warn" title="Returned for correction" className="mb-18">{returned.remarks}</Note>}

      <Card title="Report details" className="mb-18">
        <div className="tse-form-grid" style={{ gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1.3fr) minmax(0,1fr)' }}>
          <Field label="Report title" required><Input value={f.reportTitle} onChange={(e) => set('reportTitle', e.target.value)} placeholder="e.g. West Bokaro site execution" disabled={readOnly} /></Field>
          <Field label="Project" required>
            <Select value={f.projectCode} onChange={(e) => set('projectCode', e.target.value)} placeholder="Select" disabled={readOnly}
              options={[...new Set([...(f.projectCode ? [f.projectCode] : []), ...projects.map((p) => p.projectCode)])]} />
          </Field>
          <Field label="Sanction" required>
            <Select value={f.sanction} onChange={(e) => pickSanction(e.target.value)} disabled={readOnly}
              options={[{ value: '', label: 'No travel sanction' }, ...sanctions.map((s) => ({ value: s._id, label: s.requestNo }))]} />
          </Field>
          <Field label="Travel (from – to)" required>
            <div className="tse-row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <Input type="date" value={f.from} onChange={(e) => set('from', e.target.value)} disabled={readOnly} />
              <Input type="date" value={f.to} min={f.from || undefined} onChange={(e) => set('to', e.target.value)} disabled={readOnly} />
            </div>
          </Field>
          <Field label="Advance received (₹)" hint={linkedAdvance && !linkedAdvance.preLinked ? `${linkedAdvance.requestId} will be adjusted` : undefined}>
            <Input value={money(advance)} readOnly />
          </Field>
          <Field label="Location" required><Input value={f.location} onChange={(e) => set('location', e.target.value)} placeholder="Destination / site" disabled={readOnly} /></Field>
          <Field label="Mode of travel"><Select value={f.mode} onChange={(e) => set('mode', e.target.value)} options={MODES_OF_TRAVEL} disabled={readOnly} /></Field>
        </div>
      </Card>

      <Card title="Expense details" className="mb-18" actions={!readOnly && receipts.length > 0 && <button type="button" className="tse-btn sm soft" onClick={() => setReceiptPicker(true)}><i className="bi bi-receipt" />Add from receipt centre ({receipts.length})</button>}>
        <div className="tse-table-wrap">
          <table className="tse-table navy bordered compact">
            <thead>
              <tr><th style={{ width: 32 }}>#</th><th>Category <span className="req">*</span></th><th style={{ width: 150 }}>Base ₹ <span className="req">*</span></th><th style={{ width: 130 }}>GST ₹</th><th style={{ width: 160 }}>Paid by <span className="req">*</span></th><th>Bill reference <span className="req">*</span></th><th style={{ width: 150 }}>Date</th><th style={{ width: 80, textAlign: 'center' }}>Actions</th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><Select value={l.expenseType} onChange={(e) => setLine(i, 'expenseType', e.target.value)} options={EXPENSE_CATEGORIES} disabled={readOnly} /></td>
                  <td><Input type="number" min="0" value={l.amount} onChange={(e) => setLine(i, 'amount', e.target.value)} disabled={readOnly} /></td>
                  <td><Input type="number" min="0" value={l.gst} onChange={(e) => setLine(i, 'gst', e.target.value)} placeholder="0" disabled={readOnly} /></td>
                  <td><Select value={l.paidBy} onChange={(e) => setLine(i, 'paidBy', e.target.value)} options={PAID_BY} disabled={readOnly} /></td>
                  <td><Input value={l.billReference} onChange={(e) => setLine(i, 'billReference', e.target.value)} placeholder="e.g. PNR / invoice no." disabled={readOnly} /></td>
                  <td><Input type="date" value={l.date} onChange={(e) => setLine(i, 'date', e.target.value)} disabled={readOnly} /></td>
                  <td style={{ textAlign: 'center' }}>
                    {!readOnly && <button type="button" className="tse-icon-btn" style={{ width: 34, height: 34, fontSize: '0.903rem' }} title="Remove line" disabled={lines.length === 1} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><i className="bi bi-trash" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!readOnly && <button type="button" className="tse-btn outline" style={{ marginTop: 8 }} onClick={() => setLines((ls) => [...ls, line(f.from)])}><i className="bi bi-plus-lg" />Add expense line</button>}
      </Card>

      <div className="tse-split half mb-18" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
        <Card title="Additional information">
          <div className="tse-form-grid c2">
            <Field label="Tour report reference"><Input value={f.tourReportRef} onChange={(e) => set('tourReportRef', e.target.value)} placeholder="e.g. TR-25001-A-09-2026" disabled={readOnly} /></Field>
            <Field label="Shared expenses (if any)"><Input value={f.sharedExpenses} onChange={(e) => set('sharedExpenses', e.target.value)} disabled={readOnly} /></Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <div className="tse-label" style={{ marginBottom: 6 }}>Supporting bills</div>
            {!readOnly && <FileDrop inline onFiles={(fs) => setNewFiles((p) => [...p, ...fs])} accept=".pdf,.jpg,.jpeg,.png" sub="PDF, JPG or PNG · Up to 10 MB" />}
            <div className="tse-stack" style={{ gap: 6, marginTop: 8 }}>
              {attachmentsOf(existing).map((a, i) => <FileChip key={a.s3Key || i} file={a} />)}
              {newFiles.map((file, i) => <FileChip key={`n${i}`} file={file} onRemove={() => setNewFiles((p) => p.filter((_, j) => j !== i))} />)}
              {pickedReceipts.map((r) => <FileChip key={r._id} file={{ ...r.file, fileName: `${r.file?.fileName} · from receipt centre` }} onRemove={() => setPickedReceipts((p) => p.filter((x) => x._id !== r._id))} />)}
            </div>
          </div>
          <div style={{ marginTop: 8 }}><Check checked={f.declaration} onChange={(v) => set('declaration', v)} label="I confirm that expenses are official and not claimed twice." disabled={readOnly} /></div>
        </Card>
        <Card title="Claim summary (₹)">
          <div className="tse-kv-row"><span className="k">Employee expenses</span><span className="v">{money(employee)}</span></div>
          <div className="tse-kv-row"><span className="k">Company paid</span><span className="v">{money(company)}</span></div>
          <div className="tse-kv-row"><span className="k">Advance (deduct)</span><span className="v">{money(advance)}</span></div>
          <div className="tse-kv-row total"><span className="k">{net < 0 ? 'Recoverable from employee' : 'Provisional reimbursement'}</span><span className="v">{money(Math.abs(net))}</span></div>
          <div className="tse-small tse-muted" style={{ marginTop: 10 }}>Final amount subject to Accounts audit.{receiptTotal ? ` Receipts attached: ${money(receiptTotal)}.` : ''}</div>
        </Card>
      </div>

      {!readOnly && (
        <div className="tse-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="tse-btn lg outline" style={{ minWidth: 150 }} disabled={!!saving} onClick={() => save(false)}>{saving === 'draft' ? <Spin /> : null}Save draft</button>
          <button type="button" className="tse-btn lg" style={{ minWidth: 190 }} disabled={!!saving} onClick={() => save(true)}>{saving === 'submit' ? <Spin /> : null}Submit claim</button>
        </div>
      )}

      <Modal open={receiptPicker} onClose={() => setReceiptPicker(false)} size="lg" title="Add from receipt centre" sub="Unassigned receipts — selected receipts are attached to this claim and a line is pre-filled.">
        {!receipts.length ? <Empty title="No unassigned receipts" /> : (
          <div className="tse-table-wrap">
            <table className="tse-table compact">
              <thead><tr><th>Document</th><th>Type</th><th>Date</th><th>Bill no.</th><th className="num">Amount</th><th /></tr></thead>
              <tbody>
                {receipts.map((r) => {
                  const added = pickedReceipts.some((x) => x._id === r._id);
                  return (
                    <tr key={r._id}>
                      <td className="tse-ellipsis" style={{ maxWidth: 220 }}>{r.file?.fileName}</td><td>{r.documentType}</td><td>{fmtDate(r.expenseDate)}</td><td>{r.billNumber || '—'}</td><td className="num">{money(r.amount)}</td>
                      <td><button type="button" className={`tse-btn xs ${added ? 'gray' : ''}`} disabled={added} onClick={() => addFromReceipt(r)}>{added ? 'Added' : 'Add'}</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
      <div className="tse-small tse-muted" style={{ marginTop: 8 }}>{existing ? `Travel ${fmtRange(existing.travelStartDate, existing.travelEndDate)}` : ''}</div>
    </PageShell>
  );
}
